import { ApiError } from '@frontend/api-utils';
import { BlobClient } from '@frontend/blob/api';
import { EntityType } from '@frontend/common';
import { Logger } from '@frontend/logger';
import React, { createContext, useContext, useEffect, useRef, useState } from 'react';

export interface BlobInfo {
    url: string;
    blob: Blob;
    mimeType: string;
}

type BlobEntry = {
    status: 'loading' | 'loaded' | 'not_found';
    info?: BlobInfo;
    promise?: Promise<BlobInfo | null>;
};

type BlobStore = Record<string, BlobEntry>;

const buildKey = (accountId: string, entityType: EntityType, entityId: string) => `${accountId}:${entityType}:${entityId}`;

interface BlobRepositoryContext {
    getBlob: (accountId: string, entityType: EntityType, entityId: string) => Promise<BlobInfo | null>;
    getBlobStale: (accountId: string, entityType: EntityType, entityId: string) => BlobInfo | null;
    isBlobNotFound: (accountId: string, entityType: EntityType, entityId: string) => boolean;
    refreshBlob: (accountId: string, entityType: EntityType, entityId: string) => void;
    invalidate: (accountId: string, entityType: EntityType, entityId: string) => void;
}

const BlobStoreContext = createContext<BlobRepositoryContext | null>(null);

export const BlobStoreProvider = (props: { children: React.ReactNode }) => {
    const [, setVersion] = useState<number>(0);
    const storeRef = useRef<BlobStore>({});

    useEffect(() => {
        return () => {
            for (const key in storeRef.current) {
                const entry = storeRef.current[key];
                if (entry.info?.url) URL.revokeObjectURL(entry.info.url);
            }
            storeRef.current = {};
        };
    }, []);

    const fetchAndStore = (key: string, accountId: string, entityType: EntityType, entityId: string): Promise<BlobInfo | null> => {
        const promise = BlobClient.fetchBlob(accountId, entityType, entityId)
            .then<BlobInfo | null>((blob) => {
                const url = URL.createObjectURL(blob);
                const info: BlobInfo = { url, blob, mimeType: blob.type };
                const oldEntry = storeRef.current[key];
                if (oldEntry?.info?.url && oldEntry.info.url !== url) {
                    URL.revokeObjectURL(oldEntry.info.url);
                }
                storeRef.current[key] = { status: 'loaded', info };
                setVersion((v) => v + 1);
                return info;
            })
            .catch((e) => {
                if (e instanceof ApiError && e.code === 404) {
                    if (!storeRef.current[key]?.info) {
                        storeRef.current[key] = { status: 'not_found' };
                        setVersion((v) => v + 1);
                    }
                    return null;
                }
                if (!storeRef.current[key]?.info) {
                    delete storeRef.current[key];
                    setVersion((v) => v + 1);
                }
                throw e;
            });

        const current = storeRef.current[key];
        if (!current?.info && current?.status !== 'not_found') {
            storeRef.current[key] = { status: 'loading', promise };
        }

        return promise;
    };

    const getBlob = (accountId: string, entityType: EntityType, entityId: string): Promise<BlobInfo | null> => {
        const key = buildKey(accountId, entityType, entityId);
        const existing = storeRef.current[key];
        if (existing) {
            if (existing.status === 'loaded' && existing.info) return Promise.resolve(existing.info);
            if (existing.status === 'not_found') return Promise.resolve(null);
            if (existing.status === 'loading' && existing.promise) return existing.promise;
        }

        return fetchAndStore(key, accountId, entityType, entityId);
    };

    const getBlobStale = (accountId: string, entityType: EntityType, entityId: string): BlobInfo | null => {
        const key = buildKey(accountId, entityType, entityId);
        return storeRef.current[key]?.info ?? null;
    };

    const isBlobNotFound = (accountId: string, entityType: EntityType, entityId: string): boolean => {
        const key = buildKey(accountId, entityType, entityId);
        return storeRef.current[key]?.status === 'not_found';
    };

    const refreshBlob = (accountId: string, entityType: EntityType, entityId: string): void => {
        const key = buildKey(accountId, entityType, entityId);
        fetchAndStore(key, accountId, entityType, entityId).catch((e) => {
            Logger.warn('Background blob refresh failed:', e);
        });
    };

    const invalidate = (accountId: string, entityType: EntityType, entityId: string) => {
        const key = buildKey(accountId, entityType, entityId);
        const entry = storeRef.current[key];
        if (entry?.info?.url) URL.revokeObjectURL(entry.info.url);
        delete storeRef.current[key];
        setVersion((v) => v + 1);
    };

    return <BlobStoreContext.Provider value={{ getBlob, getBlobStale, isBlobNotFound, refreshBlob, invalidate }}>{props.children}</BlobStoreContext.Provider>;
};

export const useBlobRepository = () => {
    const context = useContext(BlobStoreContext);
    if (!context) {
        throw new Error('useBlobRepository must be used within a BlobStoreProvider');
    }
    return context;
};
